import React from "react";
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

//icons
import SuccessIcon from "@surgia/ui/icons/popup/success.svg";
import ActivationSuccessIcon from "@surgia/ui/icons/popup/activation.svg";

//containers
import AssignBranchForm from "@surgia/ui/containers/assignBranchForm";
import ProductFiltersModal from "../../containers/productFilterModal";

//components
import ConfirmationDialog from "@surgia/ui/rubbers/ConfirmationDialog";
import TenantInfoCard from "@surgia/ui/components/tenantInfoCard";
import GenericStepper from "@surgia/ui/components/genericModal/steps";
import GenericModal from "@surgia/ui/components/genericModal";
import BreadCrumb from "@surgia/ui/components/breadCrumb";
import Button from "@surgia/ui/components/button";
import TabNavigation from "@surgia/ui/components/tabNavigation";

//queries
import { requestQueries } from "@surgia/api/src/queryStore/admin/request";
import { tenantQueries as adminTenantQueries } from "@surgia/api/src/queryStore/admin/tenant";
import { lookupQueries } from "@surgia/api/src/queryStore/shared/lookup";

//constants
import { filterInitialData } from "./constants";
import { ADMIN_PROTECTED_ROUTES } from "../../routes";
import { TenantDetailTabs } from "./constants";
import { ProductFilters } from "../../containers/productFilterModal/constants";

//hooks
import { useGetTenantProducts } from "../../hooks/quries/useGetTenantProducts";

//tabs
import General from "./tabs/general";
import Branches from "./tabs/branches";
import Users from "./tabs/users";
import Compliance from "./tabs/compliance";
import PurchaseRequest from "./tabs/purchaseRequest";
import PurchaseOrder from "./tabs/purchaseOrder";
import Catalog from "./tabs/catalog";

const ActivationSuccess = ({ onConfirm }: { onConfirm: () => void }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  return (
    <div
      className="flex flex-col items-center justify-center"
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="p-4 mb-4">
        <img
          src={ActivationSuccessIcon}
          alt={t("shared.tenantActivation.successTitle")}
        />
      </div>
      <h2 className="text-[18px] font-bold text-center text-[#181D27]">
        {t("shared.tenantActivation.successTitle")}
      </h2>
      <p className="table-row-sub text-center mb-6">
        {t("shared.tenantActivation.successDescription")}
      </p>
      <Button
        variant="primary"
        onClick={onConfirm}
        className="w-full"
        size="lg"
      >
        {t("shared.tenantActivation.confirm")}
      </Button>
    </div>
  );
};

const TenantDetails: React.FC = () => {
  const { t } = useTranslation();
  const { id: tenantId } = useParams();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";
  const queryClient = useQueryClient();

  // states
  const [currentPage, setCurrentPage] = useState(0);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [status, setStatus] = useState<string | undefined>("ACTIVE");
  const [search, setSearch] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activationStep, setActivationStep] = useState(0);
  const [requestId, setRequestId] = useState<number | undefined>(undefined);
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [filters, setFilters] = useState<ProductFilters>(filterInitialData);
  const [openCreateTenant, setOpenCreateTenant] = useState(false);
  const [activeTab, setActiveTab] = useState("catalog");

  // Add sorting state exactly like Branches page
  const [sortState, setSortState] = useState<{
    column: string | null;
    direction: "asc" | "desc" | null;
  }>({
    column: "id", // Initial sort column
    direction: "desc", // Initial sort direction (desc for newest first)
  });

  // quieries and mutations
  const { data: tenantResponse } = useQuery(
    adminTenantQueries.queries.tenants({
      status: "ACTIVE",
      id: +tenantId!,
    })
  );

  // Extract countryId from tenant response (same as branches tab)
  const countryIdFromTenant = tenantResponse?.data[0]?.country?.id;

  // Get storefront_id from tenant response
  const storefront_id = tenantResponse?.data[0]?.storefront_id;

  // Fetch storefront currency and cache it for child pages
  const { data: storefrontCurrencyData } = useQuery({
    ...lookupQueries.queries.storefrontCurrency({
      storefront_id: Number(storefront_id) || 0,
      is_base_currency: true,
    }),
    enabled: !!storefront_id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Get currency code from storefront currency response
  const currencyCode = storefrontCurrencyData?.[0]?.currency?.code;

  const { data: tenantProductsResponse, isLoading: isTenantProductsLoading } =
    useGetTenantProducts({
      tenantId: +tenantId!,
      search,
      filters,
      currentPage,
      tenantResponse,
      size: 10,
      // Add sorting parameters exactly like Branches
      sort:
        sortState.column && sortState.direction
          ? `${sortState.column},${sortState.direction}`
          : undefined,
      currencyCode: currencyCode,
    });

  // Add sort handler exactly like Branches
  const handleSort = (newSortState: {
    column: string | null;
    direction: "asc" | "desc" | null;
  }) => {
    setSortState(newSortState);
    setCurrentPage(0);
  };

  const branchIds = tenantResponse?.data[0].branches
    .map((branch: any) => branch.id)
    ?.join(",");

  const activateRequestMutation = useMutation({
    mutationFn: (requestId: number) =>
      requestQueries.mutations.activate(requestId).mutationFn(),
    onSuccess: () => {
      // Invalidate and refetch requests data
      queryClient.invalidateQueries({ queryKey: ["requests"] });
      // Close the modal
      toast.success(t("admin.toasters.tenantActicated"), {});
      setShowActivationModal(false);
      setActivationStep(0);
    },
    onError: () => {
      toast.error(t("admin.toasters.tenantActivationFailed"), {
        description: t("admin.toasters.tryAgain"),
      });
    },
  });

  // constants
  const totalPages =
    (tenantProductsResponse?.total ?? 0 > 0)
      ? Math.ceil((tenantProductsResponse?.total ?? 0) / 10)
      : 0;

  // // Get branches and user groups for the invite form
  // const branches = getLocalStorageItem("branches", []);
  // const userGroups = userGroupsResponse?.data || [];

  // handlers
  const handleActivate = (id: string) => {
    setIsDialogOpen(true);
    setRequestId(parseInt(id));
  };

  const handleActivateTenant = () => {
    setShowActivationModal(true);
    setIsDialogOpen(false);
  };

  const handleAssignProductsBtnClick = () => {
    navigate(
      ADMIN_PROTECTED_ROUTES.PRODUCT_ASSIGNMENT?.replace(":id", tenantId!),
      {
        state: { currencyCode }, // Pass currency via navigation state
      }
    );
  };

  // useEffect
  useEffect(() => {
    setCurrentPage(0);
  }, [status]);

  return (
    <div dir={isRTL ? "rtl" : "ltr"}>
      {/* Top header section */}
      <BreadCrumb
        items={[
          { label: t("admin.tenantDetails.breadcrumb.tenants"), show: true },
          {
            label: t("admin.tenantDetails.breadcrumb.tenantDetails"),
            show: true,
          },
        ]}
      />
      <div className="flex justify-between items-center my-[10px]">
        <div>
          <h1 className="page-title ">{tenantResponse?.data[0].name}</h1>
          <p className="text-gray-500 mt-1">{tenantResponse?.data[0].id}</p>
        </div>
        <div className="flex gap-2">
          {/* <Button variant="outline" onClick={() => setShowInviteModal(true)}>
            Add User
          </Button> */}
          <Button variant="outline" onClick={() => setOpenCreateTenant(true)}>
            {t("admin.tenantDetails.actions.addBranch")}
          </Button>
        </div>
      </div>
      <TabNavigation
        tabs={TenantDetailTabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Conditional Content Rendering */}
      {activeTab === "general" && <General />}

      {activeTab === "branches" && <Branches />}

      {activeTab === "users" && <Users />}

      {activeTab === "catalog" && (
        <Catalog
          status={status}
          setStatus={setStatus}
          search={search}
          setSearch={setSearch}
          filtersModalOpen={filtersModalOpen}
          setFiltersModalOpen={setFiltersModalOpen}
          tenantProductsResponse={tenantProductsResponse}
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          handleActivate={handleActivate}
          isTenantProductsLoading={isTenantProductsLoading}
          handleAssignProductsBtnClick={handleAssignProductsBtnClick}
          handleSort={handleSort}
          sortState={sortState}
        />
      )}

      {activeTab === "compliance" && <Compliance />}

      {activeTab === "purchase-requests" && (
        <PurchaseRequest tenantId={tenantId!} branchIds={branchIds} />
      )}

      {activeTab === "purchase-orders" && <PurchaseOrder />}

      {/* popups */}
      {/* activate tenant */}
      {isDialogOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-30 backdrop-blur-sm">
          <ConfirmationDialog
            open={isDialogOpen}
            onClose={() => setIsDialogOpen(false)}
            onConfirm={handleActivateTenant}
            title={t("admin.tenantDetails.messages.activateNow")}
            description={t(
              "admin.tenantDetails.messages.activateNowDescription"
            )}
            confirmLabel={t("admin.tenantDetails.messages.activateNowLabel")}
            cancelLabel={t("admin.tenantDetails.messages.notNowLabel")}
            icon={<img src={SuccessIcon} alt="Success" />}
            additionalStyles="w-fit h-fit bg-white shadow-xl  rounded-xl"
          />
        </div>
      )}

      {showActivationModal && (
        <GenericModal
          title={t("admin.tenantDetails.messages.modalTitle")}
          subtitle={t("admin.tenantDetails.messages.modalSubtitle")}
          open={showActivationModal}
          onClose={() => setShowActivationModal(false)}
          stepper={
            <GenericStepper
              steps={[
                { label: t("admin.tenantDetails.messages.stepReviewInfo") },
                { label: t("admin.tenantDetails.messages.stepAssignBranch") },
                {
                  label: t("admin.tenantDetails.messages.stepConfirmActivate"),
                },
              ]}
              currentStep={activationStep}
            />
          }
          footer={
            activationStep === 0 && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowActivationModal(false)}
                  className="w-full text-[16px]"
                  size="lg"
                >
                  {t("admin.tenantDetails.messages.cancel")}
                </Button>
                <Button
                  variant="primary"
                  disabled={activationStep > 1}
                  onClick={() => setActivationStep(activationStep + 1)}
                  className="w-full text-[16px]"
                >
                  {t("admin.tenantDetails.messages.next")}
                </Button>
              </>
            )
          }
        >
          {activationStep === 0 && (
            <TenantInfoCard requestId={requestId?.toString()} />
          )}
          {activationStep === 1 && countryIdFromTenant && (
            <AssignBranchForm
              onSuccess={() => setActivationStep(activationStep + 1)}
              requestId={requestId}
              onClose={() => setActivationStep(activationStep - 1)}
              countryId={countryIdFromTenant}
            />
          )}
          {activationStep === 2 && (
            <ActivationSuccess
              onConfirm={() => {
                activateRequestMutation.mutate(requestId!);
                setShowActivationModal(false);
              }}
            />
          )}
        </GenericModal>
      )}

      {openCreateTenant && countryIdFromTenant && (
        <GenericModal
          title="Add branch"
          open={openCreateTenant}
          onClose={() => setOpenCreateTenant(false)}
          footer={undefined}
        >
          <AssignBranchForm
            createTenantBranch={true}
            onSuccess={() => setOpenCreateTenant(false)}
            requestId={+tenantId!}
            onClose={() => setOpenCreateTenant(false)}
            countryId={countryIdFromTenant}
          />
        </GenericModal>
      )}

      <ProductFiltersModal
        open={filtersModalOpen}
        onClose={() => setFiltersModalOpen(false)}
        onApply={() => {
          setFiltersModalOpen(false);
        }}
        filters={filters}
        setFilters={setFilters}
        tenantId={tenantId!}
        filterWithTenantCategoryParam={activeTab === "catalog"}
      />
    </div>
  );
};

export default TenantDetails;
